import React from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut, Pie } from 'react-chartjs-2';
import { DeviceUtilization } from '../../services/dashboardService';

ChartJS.register(ArcElement, Tooltip, Legend);

interface DeviceUtilizationChartProps {
  data: DeviceUtilization;
  type?: 'status' | 'device_type';
  chartType?: 'doughnut' | 'pie';
  isLoading?: boolean;
}

const DeviceUtilizationChart: React.FC<DeviceUtilizationChartProps> = ({ 
  data, 
  type = 'status',
  chartType = 'doughnut',
  isLoading = false
}) => {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
  };

  // สีสำหรับแต่ละสถานะ
  // key ต้องตรงกับ label ที่ backend ส่งมาเป๊ะ ๆ (`/dashboard/charts/device-utilization`)
  // ไม่ตรง = ตกไปใช้สีเทา #6b7280 ซึ่งซ้ำกับ 'สูญหาย' แล้วแยกชิ้นกราฟไม่ออก
  const statusColors = {
    'พร้อมใช้งาน': '#10b981', // green-500
    'กำลังเช่า': '#3b82f6',   // blue-500
    'ซ่อมบำรุง': '#f59e0b',   // amber-500
    'ชำรุด': '#ef4444',       // red-500
    'สูญหาย': '#6b7280',      // gray-500
    'จำหน่าย': '#8b5cf6',     // violet-500
    'เบิกไปใช้ภายใน': '#a855f7',              // purple-500 — ตรงกับ badge ISSUED (Stock Phase 2)
    'จองไว้ (ใบงานเตรียมเครื่อง)': '#b87333', // accent-600 — ตรงกับ badge RESERVED (PO 2026-09-08)
  };

  // สีสำหรับประเภทอุปกรณ์ (สีสันสดใส)
  const deviceTypeColors = [
    '#3b82f6', // blue-500
    '#10b981', // emerald-500
    '#f59e0b', // amber-500
    '#ef4444', // red-500
    '#8b5cf6', // violet-500
    '#06b6d4', // cyan-500
    '#84cc16', // lime-500
    '#ec4899', // pink-500
    '#6366f1', // indigo-500
    '#14b8a6', // teal-500
  ];

  const sourceData = type === 'status' ? data?.status_distribution : data?.device_type_distribution;
  
  // ตรวจสอบว่า sourceData มีข้อมูลหรือไม่ — รวมกรณีมีแถวแต่ผลรวมเป็น 0 (KD3 S2464 NEG-DASH-018:
  // โดนัทไม่มีชิ้นให้วาด เหลือแต่ legend บน canvas ว่าง ผู้ใช้ไม่รู้ว่ากราฟพังหรือไม่มีอุปกรณ์)
  const hasValue = Array.isArray(sourceData) && sourceData.some(item => Number(item.value) > 0);
  if (!sourceData || !Array.isArray(sourceData) || sourceData.length === 0 || !hasValue) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-gray-500">ไม่มีข้อมูลแสดงผล</div>
      </div>
    );
  }
  
  const chartData = {
    labels: sourceData.map(item => item.label),
    datasets: [
      {
        data: sourceData.map(item => item.value),
        backgroundColor: type === 'status' 
          ? sourceData.map(item => statusColors[item.label as keyof typeof statusColors] || '#6b7280')
          : deviceTypeColors.slice(0, sourceData.length),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverBorderWidth: 3,
      },
    ],
  };

  const ChartComponent = chartType === 'pie' ? Pie : Doughnut;

  if (isLoading) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-100 rounded-lg animate-pulse">
        <div className="text-gray-500">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  if (!data || (!data.status_distribution?.length && !data.device_type_distribution?.length)) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-gray-500">ไม่มีข้อมูล</div>
      </div>
    );
  }

  return (
    <div className="h-64" role="region" aria-label="กราฟอัตราการใช้งานอุปกรณ์">
      <ChartComponent data={chartData} options={options} aria-label="กราฟอัตราการใช้งานอุปกรณ์" />
    </div>
  );
};

export default DeviceUtilizationChart;